import type { ForqSupabaseClient } from './client';
import type {
  DeletableEntity,
  DeleteOutcome,
  DeletePlan,
  DeleteReference,
  DeleteVerdict,
  RecordDeletionGateway,
} from '@forq/contracts';
import { SupabaseOperationError } from './errors';

function messageOf(error: { readonly message: string } | null): string {
  return error?.message ?? 'Unknown Supabase error.';
}

const verdicts: readonly string[] = ['delete', 'archive', 'blocked'];

/**
 * Ein unbekanntes Urteil auf 'blocked' abbilden, nicht auf 'delete'.
 *
 * Wenn die Datenbank etwas antwortet, das diese Fassung nicht kennt, ist "nichts tun" die einzige
 * Antwort, die keinen Schaden anrichten kann. Der umgekehrte Vorgabewert würde bei jedem
 * halb ausgerollten Deploy Daten löschen.
 */
function toVerdict(value: unknown): DeleteVerdict {
  return typeof value === 'string' && verdicts.includes(value) ? (value as DeleteVerdict) : 'blocked';
}

function toReferences(value: unknown): readonly DeleteReference[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === 'object')
    .map((entry) => ({
      table: String(entry.table ?? ''),
      label: String(entry.label ?? entry.table ?? ''),
      count: Number(entry.count ?? 0),
    }));
}

export class SupabaseRecordDeletionGateway implements RecordDeletionGateway {
  constructor(private readonly client: ForqSupabaseClient) {}

  async planDeletion(tenantId: string, entity: DeletableEntity, id: string): Promise<DeletePlan> {
    const { data, error } = await this.client.rpc('record_delete_plan', {
      p_tenant_id: tenantId,
      p_entity: entity,
      p_id: id,
    });
    if (error) throw new SupabaseOperationError('delete.plan', messageOf(error));

    const row = (data ?? {}) as Record<string, unknown>;
    return {
      entity,
      id,
      label: String(row.label ?? ''),
      archived: Boolean(row.archived),
      verdict: toVerdict(row.verdict),
      references: toReferences(row.references),
      referenceCount: Number(row.referenceCount ?? 0),
      blockedReason: row.blockedReason ? String(row.blockedReason) : null,
    };
  }

  async deleteRecord(tenantId: string, entity: DeletableEntity, id: string): Promise<DeleteOutcome> {
    const { data, error } = await this.client.rpc('record_delete', {
      p_tenant_id: tenantId,
      p_entity: entity,
      p_id: id,
    });
    if (error) throw new SupabaseOperationError('delete.execute', messageOf(error));

    const row = (data ?? {}) as Record<string, unknown>;
    return {
      // Auch hier die vorsichtige Richtung: was nicht ausdrücklich 'deleted' meldet, gilt als
      // archiviert. Die Oberfläche sagt dann "archiviert" -- lieber zu vorsichtig berichtet.
      action: row.action === 'deleted' ? 'deleted' : 'archived',
      label: String(row.label ?? ''),
      references: toReferences(row.references),
      unexpected: Boolean(row.unexpected),
    };
  }

  async restoreRecord(tenantId: string, entity: DeletableEntity, id: string): Promise<void> {
    const { error } = await this.client.rpc('record_restore', {
      p_tenant_id: tenantId,
      p_entity: entity,
      p_id: id,
    });
    if (error) throw new SupabaseOperationError('delete.restore', messageOf(error));
  }
}
